import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { addDays, endOfDay, format, parseISO, startOfDay } from "date-fns";

import { DayNavigator } from "@/components/admin/DayNavigator";
import { todayIso } from "@/lib/dates";
import { Badge } from "@/components/ui/badge";
import type { BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/feedback/EmptyState";
import { toast } from "@/components/feedback/ToastHost";
import { errorMessage } from "@/lib/errors";
import { statusLabel, statusTone } from "@/lib/statusBadge";
import { listHauliers } from "@/services/api/hauliers";
import { listHaulierSnapshots, updateSnapshot } from "@/services/api/snapshots";
import { useAuth } from "@/stores/authStore";
import type { LoadSnapshot } from "@/services/api/types";

const DAYS = 7; // base day + the next 6 days

const UNSCHEDULED = "unscheduled";

/** One haulier's published loads with a Book action for locked rows. */
function HaulierLoadTable({
  rows,
  onBook,
}: {
  rows: LoadSnapshot[];
  onBook: (row: LoadSnapshot) => void;
}) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent className="overflow-x-auto p-0">
        {rows.length === 0 ? (
          <p className="px-3 py-6 text-sm text-muted-foreground">{t("haulier.noAssignedLoads")}</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                <th className="px-3 py-2">{t("haulier.loadNumber")}</th>
                <th className="px-3 py-2">{t("haulier.invoiceNumber")}</th>
                <th className="px-3 py-2">{t("haulier.customerName")}</th>
                <th className="px-3 py-2">{t("haulier.customerAddress")}</th>
                <th className="px-3 py-2">{t("haulier.customerPostcode")}</th>
                <th className="px-3 py-2">{t("haulier.collectionDate")}</th>
                <th className="px-3 py-2">{t("haulier.collectionTime")}</th>
                <th className="px-3 py-2">{t("haulier.rateCardNo")}</th>
                <th className="px-3 py-2">{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-b last:border-0">
                  <td className="px-3 py-2 font-semibold">{row.load_ref}</td>
                  <td className="px-3 py-2 font-mono">{row.invoice_reference ?? "—"}</td>
                  <td className="px-3 py-2">{row.customer_name}</td>
                  <td className="px-3 py-2">{row.customer_address ?? "—"}</td>
                  <td className="px-3 py-2">{row.customer_postcode ?? "—"}</td>
                  <td className="px-3 py-2">{row.collection_date ?? "—"}</td>
                  <td className="px-3 py-2">{row.collection_time ?? "—"}</td>
                  <td className="px-3 py-2">{row.rate_card_no ?? "—"}</td>
                  <td className="px-3 py-2">
                    {row.status === "locked" ? (
                      <Button size="sm" onClick={() => onBook(row)}>
                        {t("haulier.book")}
                      </Button>
                    ) : (
                      <Badge variant={statusTone(row.status) as BadgeProps["variant"]}>
                        {statusLabel(row.status)}
                      </Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

/** Haulier portal: published snapshot loads with a Book action. */
export function HaulierDashboardPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  // Administrators have no bound haulier and see every haulier's loads.
  const isAdmin = user?.haulier_id == null;
  const [baseDay, setBaseDay] = useState(todayIso());

  const { data, isLoading } = useQuery({
    queryKey: ["haulier-snapshots", baseDay],
    queryFn: listHaulierSnapshots,
  });
  const hauliers = useQuery({
    queryKey: ["hauliers", "all"],
    queryFn: () => listHauliers({ limit: 500 }),
    enabled: isAdmin,
  });

  const book = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Parameters<typeof updateSnapshot>[1] }) =>
      updateSnapshot(id, data),
    onSuccess: () => {
      toast.success(t("haulier.book"));
      void queryClient.invalidateQueries({ queryKey: ["haulier-snapshots"] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const today = startOfDay(parseISO(baseDay));
  const horizon = addDays(today, DAYS - 1);

  // Rows within the visible window, sorted by haulier, then collection time.
  const windowed = useMemo(() => {
    const rows = (data ?? []).filter((row) => {
      if (!row.collection_date) return true;
      const date = parseISO(row.collection_date);
      return date >= today && date <= endOfDay(horizon);
    });
    rows.sort((a, b) => {
      const aName = a.haulier_name ?? "";
      const bName = b.haulier_name ?? "";
      if (aName !== bName) return aName.localeCompare(bName);
      const aTime = a.collection_time ?? "";
      const bTime = b.collection_time ?? "";
      if (aTime !== bTime) return aTime.localeCompare(bTime);
      return a.load_ref.localeCompare(b.load_ref);
    });
    return rows;
  }, [data, today, horizon]);

  // A haulier sees their own loads grouped by day across the week.
  const byDay = useMemo(() => {
    const map = new Map<string, LoadSnapshot[]>();
    for (const row of windowed) {
      const key = row.collection_date ?? UNSCHEDULED;
      const bucket = map.get(key) ?? [];
      bucket.push(row);
      map.set(key, bucket);
    }
    return map;
  }, [windowed]);
  const days = useMemo(
    () =>
      Array.from(byDay.keys()).sort((a, b) => {
        if (a === UNSCHEDULED) return 1;
        if (b === UNSCHEDULED) return -1;
        return a.localeCompare(b);
      }),
    [byDay],
  );

  // Administrator view: every haulier, alphabetical, one table each.
  const adminHauliers = useMemo(() => {
    const names = new Map<number, string>();
    for (const h of hauliers.data?.items ?? []) names.set(h.id, h.name);
    for (const row of data ?? []) {
      if (row.haulier_id != null && row.haulier_name) names.set(row.haulier_id, row.haulier_name);
    }
    const list = [...names.entries()].map(([id, name]) => ({ id, name }));
    list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [hauliers.data, data]);
  const rowsByHaulier = useMemo(() => {
    const map = new Map<number, LoadSnapshot[]>();
    for (const row of windowed) {
      if (row.haulier_id == null) continue;
      const bucket = map.get(row.haulier_id) ?? [];
      bucket.push(row);
      map.set(row.haulier_id, bucket);
    }
    return map;
  }, [windowed]);

  const dayLabel = (key: string): string =>
    key === UNSCHEDULED
      ? t("haulier.unscheduled")
      : format(parseISO(key), "EEEE d MMMM yyyy");

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold">{t("haulier.title")}</h1>
      </div>
      <DayNavigator day={baseDay} onChange={setBaseDay} />

      {isLoading ? (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            {t("common.loading")}
          </CardContent>
        </Card>
      ) : isAdmin ? (
        // One table per haulier, in alphabetical order.
        adminHauliers.length === 0 ? (
          <EmptyState title={t("haulier.noAssignedLoads")} />
        ) : (
          <div className="space-y-6">
            {adminHauliers.map((haulier) => (
              <section key={haulier.id} className="space-y-2">
                <div className="border-b pb-1 text-lg font-semibold">{haulier.name}</div>
                <HaulierLoadTable
                  rows={rowsByHaulier.get(haulier.id) ?? []}
                  onBook={(row) => book.mutate({ id: row.id, data: { status: "booked" } })}
                />
              </section>
            ))}
          </div>
        )
      ) : days.length === 0 ? (
        <EmptyState title={t("haulier.noAssignedLoads")} />
      ) : (
        days.map((key) => (
          <section key={key} className="space-y-2">
            <div className="border-b pb-1 text-lg font-semibold">{dayLabel(key)}</div>
            <HaulierLoadTable
              rows={byDay.get(key) ?? []}
              onBook={(row) => book.mutate({ id: row.id, data: { status: "booked" } })}
            />
          </section>
        ))
      )}
    </div>
  );
}
